import { ApolloLink } from "@apollo/client/core";
import { onError } from "@apollo/client/link/error";
import {
  NETWORK_ERROR_MESSAGES_ZH,
  DEFAULT_NETWORK_ERROR_MESSAGE,
  DEFAULT_REQUEST_ERROR_MESSAGE,
  hasChinese,
} from "../common/core/request/error-messages";

interface ServerErrorLike extends Error {
  statusCode?: number;
}

export interface GraphQLErrorHandlerOptions {
  networkErrorMessages?: Record<number, string>;
  defaultNetworkErrorMessage?: string;
  defaultGraphQLErrorMessage?: string;
  isLoginExpired?: (error: { message?: string; statusCode?: number }) => boolean;
  onLoginExpired?: () => void;
  shouldSkipRewrite?: (message: string) => boolean;
}

/**
 * GraphQL 错误拦截器：
 * - graphQLErrors：含中文的透传（后端业务错误），不含中文的改写为默认消息（框架/网关错误）；
 *   结构化错误可通过 shouldSkipRewrite 跳过改写。
 * - networkError：按 HTTP statusCode 映射中文消息，无条件覆盖。
 * 复用 error-messages.ts 的中英文案与 hasChinese，与 axios 侧 getErrorMessage 保持一致。
 */
export function createGraphQLErrorHandler(options?: GraphQLErrorHandlerOptions): ApolloLink {
  const networkErrorMessages = {
    ...NETWORK_ERROR_MESSAGES_ZH,
    ...options?.networkErrorMessages,
  };
  const defaultNetworkErrorMessage = options?.defaultNetworkErrorMessage ?? DEFAULT_NETWORK_ERROR_MESSAGE.zh;
  const defaultGraphQLErrorMessage = options?.defaultGraphQLErrorMessage ?? DEFAULT_REQUEST_ERROR_MESSAGE.zh;
  const isLoginExpired = options?.isLoginExpired;
  const onLoginExpired = options?.onLoginExpired;
  const shouldSkipRewrite = options?.shouldSkipRewrite;

  return onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      let hasDefaultMessage = false;
      for (const error of graphQLErrors) {
        console.error(`GraphQL 错误: ${error.message}, Location: ${error.locations}, Path: ${error.path}`);
        if (isLoginExpired?.({ message: error.message })) {
          onLoginExpired?.();
          return;
        }
        if (!hasChinese(error.message) && !shouldSkipRewrite?.(error.message)) {
          if (!hasDefaultMessage) {
            (error as { message: string }).message = defaultGraphQLErrorMessage;
            hasDefaultMessage = true;
          } else {
            (error as { message: string }).message = " ";
          }
        }
      }
    }

    if (networkError) {
      console.error(`网络错误: ${networkError}`);
      if (isLoginExpired?.(networkError)) {
        onLoginExpired?.();
        return;
      }
      const statusCode = (networkError as ServerErrorLike).statusCode;
      networkError.message = networkErrorMessages[statusCode!] || defaultNetworkErrorMessage;
    }
  });
}
